import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { SECTORS } from "../lib/sectors";
import { useConfirm } from "../components/useConfirm";
import { FormatSelector, FORMATS } from "./FormatSelector";
import { PhoneMockup } from "./PhoneMockup";
import { TelegramButton } from "./TelegramButton";
import { ImagePlus, Loader2, Download, Trash2, Sparkles, Wand2, Smartphone } from "lucide-react";

const PROVIDERS = [
  { id: "gemini", label: "Gemini · Nano Banana" },
  { id: "openai", label: "OpenAI · GPT Image 1" },
];

const PLACEHOLDERS = {
  wellness: "Descrivi l'immagine: es. colazione proteica luminosa su tavolo in legno, stile lifestyle…",
  book: "Descrivi l'immagine: es. bosco al tramonto con un libro aperto, atmosfera poetica…",
  ai: "Descrivi l'immagine: es. copertina minimale per tutorial AI, palette viola e nero, stile editoriale…",
};

export function ImageStudio({ prompt, setPrompt, sector = "ai" }) {
  const s = SECTORS[sector];
  const [provider, setProvider] = useState("gemini");
  const [format, setFormat] = useState("post");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/images", { params: { sector } }).then((r) => setImages(r.data)).catch(() => {});
  useEffect(() => { load(); }, [sector]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = (e) => { if (e.detail?.sector === sector) load(); };
    window.addEventListener("cs:images-updated", h);
    return () => window.removeEventListener("cs:images-updated", h);
  }, [sector]); // eslint-disable-line react-hooks/exhaustive-deps
  const [previewImg, setPreviewImg] = useState(null);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Scrivi una descrizione dell'immagine"); return; }
    setLoading(true);
    try {
      await api.post("/ai/image", { prompt: prompt.trim(), provider, format, sector }, { timeout: 150000 });
      toast.success("Immagine generata!");
      load();
    } catch (e) {
      toast.error("Errore nella generazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const download = async (img) => {
    try {
      const res = await fetch(`${API}/images/${img.id}/file`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = (img.mime_type || "image/png").split("/")[1] || "png";
      a.href = url; a.download = `${img.format || "immagine"}-${img.id.slice(0, 8)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  const remove = async (img) => {
    if (!(await confirm({ title: "Eliminare l'immagine?", description: "L'immagine verrà rimossa dalla galleria.", confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/images/${img.id}`); toast.success("Immagine eliminata"); load();
  };

  return (
    <div className="glass rounded-2xl p-5" style={{ borderTop: `3px solid ${s.hex}` }} data-testid={`image-studio-${sector}`}>
      <div className="flex items-center gap-2 mb-1">
        <ImagePlus className="h-5 w-5" style={{ color: s.hex }} />
        <h3 className="font-display font-bold">Generatore immagini</h3>
      </div>
      <p className="text-xs text-slate-400 mb-4">Scegli il formato: l'immagine viene ritagliata esattamente a 1:1, 9:16 o 16:9.</p>

      <FormatSelector value={format} onChange={setFormat} color={s.hex} testid={`image-format-${sector}`} />

      <textarea data-testid={`image-prompt-input-${sector}`} value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
        placeholder={PLACEHOLDERS[sector]}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none resize-none" />

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <select data-testid={`image-provider-select-${sector}`} value={provider} onChange={(e) => setProvider(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none">
          {PROVIDERS.map((p) => <option key={p.id} value={p.id} className="bg-slate-900">{p.label}</option>)}
        </select>
        <button data-testid={`generate-image-button-${sector}`} onClick={generate} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
          style={{ background: s.hex }}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {loading ? "Genero…" : "Genera immagine"}
        </button>
      </div>

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: s.hex }}>
          <Sparkles className="h-4 w-4 animate-pulse" /> La generazione può richiedere fino a un minuto…
        </div>
      )}

      <div className="mt-5">
        {images.length === 0 && !loading ? (
          <p className="text-sm text-slate-500">Nessuna immagine ancora. Genera la prima!</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 items-start" data-testid={`image-gallery-${sector}`}>
            {images.map((img) => <ImageCard key={img.id} img={img} onDownload={download} onDelete={remove} onPreview={setPreviewImg} />)}
          </div>
        )}
      </div>
      <PhoneMockup open={!!previewImg} onOpenChange={(o) => !o && setPreviewImg(null)} color={s.hex}
        imageUrl={previewImg ? `${API}/images/${previewImg.id}/file` : null} imageId={previewImg?.id || ""}
        onDownload={previewImg ? () => download(previewImg) : null} testid={`gallery-mockup-${sector}`} />
      {ConfirmUI}
    </div>
  );
}

function ImageCard({ img, onDownload, onDelete, onPreview }) {
  const f = FORMATS.find((x) => x.id === img.format) || FORMATS[0];
  return (
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
      className="group relative rounded-xl overflow-hidden border border-white/10 bg-black/30" data-testid={`image-${img.id}`}>
      <img src={`${API}/images/${img.id}/file`} alt={img.prompt} className="w-full object-cover" style={{ aspectRatio: f.aspect }} loading="lazy" />
      <span className="absolute bottom-2 left-2 text-[10px] px-1.5 py-0.5 rounded-md bg-black/70 text-slate-200" data-testid={`image-format-badge-${img.id}`}>
        {f.label}{img.width ? ` · ${img.width}×${img.height}` : ""}
      </span>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
        <span className="self-start text-[10px] px-1.5 py-0.5 rounded-md bg-black/60 text-slate-200 capitalize">{img.provider}</span>
        <div className="flex gap-1.5 self-end">
          {img.format === "story" && (
            <button data-testid={`preview-image-${img.id}`} onClick={() => onPreview(img)} title="Anteprima Story" className="h-8 w-8 grid place-items-center rounded-lg bg-white/90 text-slate-900 hover:bg-white"><Smartphone className="h-4 w-4" /></button>
          )}
          <TelegramButton testid={`telegram-image-${img.id}`} label="" getPayload={async () => ({ imageId: img.id })}
            className="h-8 w-8 grid place-items-center rounded-lg bg-[#229ED9] text-white hover:opacity-90 disabled:opacity-60 [&>svg]:mx-auto" />
          <button data-testid={`download-image-${img.id}`} onClick={() => onDownload(img)} className="h-8 w-8 grid place-items-center rounded-lg bg-white/90 text-slate-900 hover:bg-white"><Download className="h-4 w-4" /></button>
          <button data-testid={`delete-image-${img.id}`} onClick={() => onDelete(img)} className="h-8 w-8 grid place-items-center rounded-lg bg-rose-500/90 text-white hover:bg-rose-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </motion.div>
  );
}
